import { type Currency, PROMOTION_TYPES, SearchModes } from '@/domainLogic/index.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation.tsx';
import type { SearchInfoData } from '@/app/search/types.ts';
import type { CommonData } from '@/app/common/createCommonData';
import type { StarfishIconRef } from '@/app/common/icons/starfish';
import { formatPrice } from '../../../../../utils/formatting.ts';
import type { Localization } from '../../../../../common/Localization';
import type { DealCard } from '../../../../../utils/buildDealCards/dealCardTypes';
import type { Dot } from '../../../ListContainer/CardsGrid/types';
import { getPriceDetails } from '../../../../Card/utils/getPriceDetails';
import { getDotPromotionType } from '../../../../Card/utils/getPromotionsUtils';
import { sumClasses } from '../../../../../utils/misc';

type PromotionConfig = {
  /** A named icon, or a multi-colour asset path relative to `icons/` (the loyalty and MyPrice logos). */
  icon?: StarfishIconRef | string;
  isActive?: boolean;
};

export const getPromotionConfig = (
  promotionType: PROMOTION_TYPES | undefined,
  lastMinuteDeals: boolean,
  evolutionEnabled = false
): PromotionConfig => {
  switch (promotionType) {
    case PROMOTION_TYPES.MY_PRICE:
      return { icon: 'my_price_white.svg', isActive: true };
    case PROMOTION_TYPES.LOYALTY:
      return { icon: 'color/loyalty.svg', isActive: true };
    case PROMOTION_TYPES.JANUARY_SALE:
    case PROMOTION_TYPES.FLASH_SALES:
      if (lastMinuteDeals) {
        return {
          icon: { category: 'Brands and products', name: 'icon-lastminute-deals', color: 'iconOnDark' },
          isActive: true,
        };
      }
      return {
        icon: {
          category: 'Brands and products',
          name: 'icon-flash-sales',
          color: evolutionEnabled ? 'iconPrimary' : 'iconOnDark',
        },
        isActive: true,
      };
    case PROMOTION_TYPES.TO_LASTMINUTE:
      return { isActive: true };
    default:
      return {};
  }
};

export const getDotPrice = ({
  dot,
  searchInfoData,
  localization,
  showTotalPrice,
  travellersCount,
}: {
  dot: Dot;
  searchInfoData: SearchInfoData;
  localization: Localization;
  showTotalPrice: boolean;
  travellersCount: number;
}) => {
  const isDP = searchInfoData.searchMode === SearchModes.DP;
  return showTotalPrice && isDP && dot.priceValue != null
    ? formatPrice({ price: dot.priceValue * Math.max(1, travellersCount), localization })
    : dot.price;
};

const getMarkerPrice = ({
  dealCard,
  searchInfoData,
  localization,
  commonData,
  // currency,
  t,
  isMobile,
  showTotalPrice,
}: {
  dealCard: DealCard;
  searchInfoData: SearchInfoData;
  commonData: CommonData;
  localization: Localization;
  currency: Currency;
  t: TranslationFunction;
  isMobile: boolean;
  showTotalPrice: boolean;
}) => {
  const { price, totalPrice } = getPriceDetails({
    dealCard,
    searchInfoData,
    localization,
    commonData,
    isMobile,
    isOverlay: true,
    t,
  });

  if (dealCard.product?.dates?.soldOut) return t('vacation.oss.map.na');
  return showTotalPrice ? totalPrice : price;
};

export const getClassName = ({
  promotionType,
  isWaiting,
  lastMinuteDeals,
  evolutionEnabled = false,
}: {
  promotionType: PROMOTION_TYPES | undefined;
  isWaiting: boolean;
  lastMinuteDeals: boolean;
  evolutionEnabled?: boolean;
}) => {
  const { icon, isActive } = getPromotionConfig(promotionType, lastMinuteDeals, evolutionEnabled);
  const markerPromotionClass =
    promotionType && isActive && !isWaiting ? `marker_promotion_${promotionType.toLowerCase()}` : ``;
  return {
    className: sumClasses(['marker', markerPromotionClass, `${icon && !isWaiting ? 'has-icon' : ''}`]),
    icon,
    promotionType,
  };
};

const getMarkerPromotionType = (promotionTypes: PROMOTION_TYPES[], isDP: boolean, dot?: Dot) => {
  if (dot) {
    return getDotPromotionType({ dot });
  }

  if (promotionTypes?.includes(PROMOTION_TYPES.FLASH_SALES)) {
    return PROMOTION_TYPES.FLASH_SALES;
  }

  if (!isDP) {
    if (promotionTypes?.includes(PROMOTION_TYPES.LOYALTY)) {
      return PROMOTION_TYPES.LOYALTY;
    }

    if (promotionTypes?.includes(PROMOTION_TYPES.MY_PRICE)) {
      return PROMOTION_TYPES.MY_PRICE;
    }
  }

  return undefined;
};

export const getMarkerProps = ({
  dealCard,
  dot,
  searchInfoData,
  commonData,
  localization,
  currency,
  t,
  isWaiting,
  isMobile,
  showTotalPrice,
  isEvoLayout,
  totalPriceMode = false,
  travellersCount = 1,
}: {
  dealCard?: DealCard;
  dot?: Dot;
  searchInfoData: SearchInfoData;
  commonData: CommonData;
  localization: Localization;
  currency: Currency;
  t: TranslationFunction;
  isWaiting: boolean;
  isMobile: boolean;
  showTotalPrice: boolean;
  isEvoLayout: boolean;
  totalPriceMode?: boolean;
  travellersCount?: number;
}) => {
  const { searchMode } = searchInfoData;
  const isDP = searchMode === SearchModes.DP;
  const promotionType = getMarkerPromotionType(dealCard?.promotionTypes || [], isDP, dot);
  const { className, icon } = getClassName({
    promotionType,
    isWaiting,
    lastMinuteDeals: localization.configValuesApp.lastMinuteDeals,
    evolutionEnabled: isEvoLayout,
  });

  if (dot) {
    const { hotelId, cityId, lat: latMarker, lng: lonMarker } = dot;
    // Dots follow the funnel total-price mode, so a family search now shows the group total on them.
    const price = getDotPrice({
      dot,
      searchInfoData,
      localization,
      showTotalPrice: totalPriceMode && showTotalPrice,
      travellersCount,
    });
    return { hotelId, cityId, latMarker, lonMarker, price, className, icon, promotionType };
  }

  const { hotelId, latitude: latMarker, longitude: lonMarker } = (dealCard as DealCard).product.accommodation;
  const price = getMarkerPrice({
    dealCard: dealCard || ({} as DealCard),
    searchInfoData,
    localization,
    currency,
    t,
    isMobile,
    commonData,
    showTotalPrice,
  });

  return { hotelId, latMarker, lonMarker, price, className, icon, promotionType };
};
